import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Database connection status types
export type DatabaseType = 'sqlite' | 'postgresql' | 'unknown';
export type ConnectionStatus = 'connecting' | 'connected' | 'error' | 'disconnected';

interface DatabaseInfo {
  type: DatabaseType;
  status: ConnectionStatus;
  version?: string;
  timescaleAvailable?: boolean;
  features?: {
    hypertables: boolean;
    continuousAggregates: boolean;
    compression: boolean;
  };
  lastHealthCheck?: Date;
  error?: string;
}

interface DatabaseContextType {
  databaseInfo: DatabaseInfo;
  isPostgreSQL: boolean;
  isSQLite: boolean;
  hasTimescaleDB: boolean;
  refreshStatus: () => Promise<void>;
  apiUrl: string;
}

const DatabaseContext = createContext<DatabaseContextType | undefined>(undefined);

interface DatabaseProviderProps {
  children: ReactNode;
}

export const DatabaseProvider: React.FC<DatabaseProviderProps> = ({ children }) => {
  const [databaseInfo, setDatabaseInfo] = useState<DatabaseInfo>({
    type: 'unknown',
    status: 'connecting'
  });

  // Determine API URL based on environment
  const getApiUrl = () => {
    if (typeof window !== 'undefined') {
      // Browser environment
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      
      // Check if we're on Vite dev server (port 5173)
      if (window.location.port === '5173') {
        // Dev server - backend is on port 3000 of same host
        return `${protocol}//${hostname}:3000/api`;
      }
      
      // Production - use same origin (nginx proxy)
      const port = window.location.port ? `:${window.location.port}` : '';
      return `${protocol}//${hostname}${port}/api`;
    } else {
      // Server-side or development
      return process.env.API_URL || 'http://localhost:3000/api';
    }
  };

  const apiUrl = getApiUrl();

  const detectDatabaseType = async (): Promise<DatabaseInfo> => {
    try {
      // Try health check endpoint first
      const healthResponse = await fetch(`${apiUrl}/health`);
      const healthData = await healthResponse.json();
      
      if (healthData.database) {
        // PostgreSQL backend detected
        return {
          type: 'postgresql',
          status: 'connected',
          version: healthData.database_version,
          lastHealthCheck: new Date()
        };
      }
      
      // Check for TimescaleDB features if PostgreSQL
      if (healthData.status?.includes('PostgreSQL') || healthData.database === 'PostgreSQL + TimescaleDB') {
        try {
          // Try to fetch TimescaleDB specific health info
          const detailedHealthResponse = await fetch(`${apiUrl}/health/detailed`);
          if (detailedHealthResponse.ok) {
            const detailedHealth = await detailedHealthResponse.json();
            
            return {
              type: 'postgresql',
              status: 'connected',
              version: detailedHealth.database?.version,
              timescaleAvailable: detailedHealth.timescaledb?.available || false,
              features: {
                hypertables: detailedHealth.timescaledb?.status === 'active',
                continuousAggregates: detailedHealth.checks?.timescaledb?.continuous_aggregates?.length > 0,
                compression: detailedHealth.checks?.timescaledb?.compression_settings?.length > 0
              },
              lastHealthCheck: new Date()
            };
          }
        } catch (err) {
          // Detailed health check failed, but basic connection works
          console.warn('Could not fetch detailed database info:', err);
        }
        
        return {
          type: 'postgresql',
          status: 'connected',
          timescaleAvailable: false,
          lastHealthCheck: new Date()
        };
      }
      
      // Default to SQLite for backwards compatibility
      return {
        type: 'sqlite',
        status: 'connected',
        lastHealthCheck: new Date()
      };
      
    } catch (error) {
      console.error('Database detection failed:', error);
      return {
        type: 'unknown',
        status: 'error',
        error: error instanceof Error ? error.message : 'Connection failed',
        lastHealthCheck: new Date()
      };
    }
  };

  const refreshStatus = async () => {
    setDatabaseInfo(prev => ({ ...prev, status: 'connecting' }));
    const newInfo = await detectDatabaseType();
    setDatabaseInfo(newInfo);
  };

  // Initial database detection
  useEffect(() => {
    refreshStatus();
    
    // Set up periodic health checks (every 5 minutes)
    const interval = setInterval(refreshStatus, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [apiUrl]);

  // Derived state
  const isPostgreSQL = databaseInfo.type === 'postgresql';
  const isSQLite = databaseInfo.type === 'sqlite';
  const hasTimescaleDB = isPostgreSQL && databaseInfo.timescaleAvailable === true;

  const contextValue: DatabaseContextType = {
    databaseInfo,
    isPostgreSQL,
    isSQLite,
    hasTimescaleDB,
    refreshStatus,
    apiUrl
  };

  return (
    <DatabaseContext.Provider value={contextValue}>
      {children}
    </DatabaseContext.Provider>
  );
};

export const useDatabaseContext = (): DatabaseContextType => {
  const context = useContext(DatabaseContext);
  if (!context) {
    throw new Error('useDatabaseContext must be used within a DatabaseProvider');
  }
  return context;
};

// Hook for database-specific API calls
export const useDatabaseAPI = () => {
  const { apiUrl, isPostgreSQL, hasTimescaleDB } = useDatabaseContext();

  const getEndpoint = (endpoint: string) => `${apiUrl}${endpoint}`;

  // Enhanced endpoints available with PostgreSQL + TimescaleDB
  const analyticsEndpoints = {
    // Basic endpoints (available on both SQLite and PostgreSQL)
    productionDashboard: (hours: number = 8) => getEndpoint(`/analytics/production-dashboard?hours=${hours}`),
    yieldAnalysis: (days: number = 7, lineId?: string) => {
      const params = new URLSearchParams({ days: days.toString() });
      if (lineId) params.append('line_id', lineId);
      return getEndpoint(`/analytics/yield-analysis?${params}`);
    },
    
    // Enhanced endpoints (PostgreSQL + TimescaleDB only)
    ...(isPostgreSQL && {
      employeePerformance: (days: number = 30, minHours: number = 4) => 
        getEndpoint(`/analytics/employee-performance?days=${days}&min_hours=${minHours}`),
      anomalyDetection: (hours: number = 24, sensitivity: number = 2) => 
        getEndpoint(`/analytics/anomaly-detection?hours=${hours}&sensitivity=${sensitivity}`),
      productionForecast: (days: number = 7, forecastHours: number = 8) => 
        getEndpoint(`/analytics/production-forecast?days=${days}&forecast_hours=${forecastHours}`),
      equipmentEfficiency: (days: number = 30) => 
        getEndpoint(`/analytics/equipment-efficiency?days=${days}`),
      
      // TimescaleDB specific endpoints
      ...(hasTimescaleDB && {
        recentStationData: (hours: number = 24) => 
          getEndpoint(`/station-data/recent/${hours}`),
        yieldTrend: (days: number = 7) => 
          getEndpoint(`/analytics/yield-trend/${days}`),
        shiftEfficiency: (date: string) => 
          getEndpoint(`/analytics/shift-efficiency/${date}`)
      })
    })
  };

  return {
    getEndpoint,
    analyticsEndpoints,
    capabilities: {
      basicAnalytics: true,
      advancedAnalytics: isPostgreSQL,
      timeSeriesAnalytics: hasTimescaleDB,
      realTimeMonitoring: hasTimescaleDB
    }
  };
};

// Database feature detection hook
export const useDatabaseFeatures = () => {
  const { databaseInfo, isPostgreSQL, hasTimescaleDB } = useDatabaseContext();

  return {
    // Core features
    crud: true,
    basicReports: true,
    
    // Advanced features
    concurrentUsers: isPostgreSQL,
    transactions: isPostgreSQL,
    advancedIndexing: isPostgreSQL,
    
    // TimescaleDB features
    timeSeriesOptimization: hasTimescaleDB,
    automaticCompression: hasTimescaleDB && databaseInfo.features?.compression,
    continuousAggregates: hasTimescaleDB && databaseInfo.features?.continuousAggregates,
    hypertables: hasTimescaleDB && databaseInfo.features?.hypertables,
    
    // Performance characteristics
    scalability: isPostgreSQL ? 'high' : 'medium',
    queryPerformance: hasTimescaleDB ? 'excellent' : isPostgreSQL ? 'good' : 'adequate',
    dataRetention: hasTimescaleDB ? 'automatic' : 'manual'
  };
};